import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createPlatformTenantLicense,
  deletePlatformTenantLicense,
  getPlatformTenant,
  getPlatformTenantLicense,
  mapLaravelError,
  updatePlatformTenantLicense,
} from '../../services/platformTenantsApi';
import { formatLicenseWindow, licenseStatusColor, licenseStatusLabel } from '../../utils/tenantLicense';

const TENANT_BASE_PATH = '/super-admin/platform/tenants';

const emptyRoleLimit = () => ({
  role: '',
  active_user_limit: '',
});

const emptyForm = {
  valid_from: '',
  valid_to: '',
  total_active_users_limit: '',
  notes: '',
  role_limits: [],
};

const normalizeRole = (value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

export default function TenantLicenseFormPage() {
  const navigate = useNavigate();
  const { id, licenseId } = useParams();
  const tenantId = Number(id || 0);
  const editingLicenseId = Number(licenseId || 0);
  const isEdit = Boolean(editingLicenseId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [tenant, setTenant] = useState(null);
  const [currentLicense, setCurrentLicense] = useState(null);
  const [licenseStatus, setLicenseStatus] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [loadedLicense, setLoadedLicense] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const tenantPayload = await getPlatformTenant(tenantId);
      const tenantRecord = tenantPayload?.tenant || null;

      let nextForm = emptyForm;
      if (isEdit) {
        const licensePayload = await getPlatformTenantLicense(tenantId, editingLicenseId);
        const license = licensePayload?.data || null;
        setLoadedLicense(license);

        nextForm = {
          valid_from: license?.valid_from || '',
          valid_to: license?.valid_to || '',
          total_active_users_limit: license?.total_active_users_limit != null
            ? String(license.total_active_users_limit)
            : '',
          notes: license?.notes || '',
          role_limits: Array.isArray(license?.role_limits)
            ? license.role_limits.map((roleLimit) => ({
              role: roleLimit.role || '',
              active_user_limit: roleLimit.active_user_limit != null
                ? String(roleLimit.active_user_limit)
                : '',
            }))
            : [],
        };
      } else {
        setLoadedLicense(null);
      }

      setTenant(tenantRecord);
      setCurrentLicense(tenantPayload?.current_license || null);
      setLicenseStatus(tenantPayload?.license_status || '');
      setForm(nextForm);
    } catch (requestError) {
      const mapped = mapLaravelError(requestError);
      setError(mapped.message);
      setFieldErrors(mapped.fieldErrors);
    } finally {
      setLoading(false);
    }
  }, [editingLicenseId, isEdit, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const pageTitle = useMemo(
    () => (isEdit ? 'Edit Future License' : 'Add or Renew License'),
    [isEdit]
  );

  const validateForm = () => {
    const nextErrors = {};

    if (!form.valid_from) {
      nextErrors.valid_from = 'Start date is required.';
    }
    if (!form.valid_to) {
      nextErrors.valid_to = 'End date is required.';
    }

    const totalLimit = Number(form.total_active_users_limit);
    if (!form.total_active_users_limit || !Number.isInteger(totalLimit) || totalLimit < 1) {
      nextErrors.total_active_users_limit = 'Enter a positive active-user limit.';
    }

    const seenRoles = new Set();
    form.role_limits.forEach((roleLimit, index) => {
      const role = normalizeRole(roleLimit.role);
      const limit = Number(roleLimit.active_user_limit);

      if (!role) {
        nextErrors[`role_limits.${index}.role`] = 'Role is required.';
      } else if (seenRoles.has(role)) {
        nextErrors[`role_limits.${index}.role`] = 'Each role can only appear once.';
      } else {
        seenRoles.add(role);
      }

      if (!roleLimit.active_user_limit || !Number.isInteger(limit) || limit < 1) {
        nextErrors[`role_limits.${index}.active_user_limit`] = 'Enter a positive limit.';
      }
    });

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateRoleLimit = (index, key, value) => {
    setForm((previous) => ({
      ...previous,
      role_limits: previous.role_limits.map((roleLimit, currentIndex) => (
        currentIndex === index
          ? { ...roleLimit, [key]: value }
          : roleLimit
      )),
    }));
  };

  const addRoleLimit = () => {
    setForm((previous) => ({
      ...previous,
      role_limits: [...previous.role_limits, emptyRoleLimit()],
    }));
  };

  const removeRoleLimit = (index) => {
    setForm((previous) => ({
      ...previous,
      role_limits: previous.role_limits.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      valid_from: form.valid_from,
      valid_to: form.valid_to,
      total_active_users_limit: Number(form.total_active_users_limit),
      notes: form.notes.trim() || null,
      role_limits: form.role_limits.map((roleLimit) => ({
        role: normalizeRole(roleLimit.role),
        active_user_limit: Number(roleLimit.active_user_limit),
      })),
    };

    try {
      if (isEdit) {
        await updatePlatformTenantLicense(tenantId, editingLicenseId, payload);
      } else {
        await createPlatformTenantLicense(tenantId, payload);
      }

      navigate(`${TENANT_BASE_PATH}/${tenantId}`);
    } catch (requestError) {
      const mapped = mapLaravelError(requestError);
      setError(mapped.message);
      setFieldErrors(mapped.fieldErrors);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !loadedLicense || !window.confirm(`Delete future license ${formatLicenseWindow(loadedLicense)}?`)) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await deletePlatformTenantLicense(tenantId, editingLicenseId);
      navigate(`${TENANT_BASE_PATH}/${tenantId}`);
    } catch (requestError) {
      const mapped = mapLaravelError(requestError);
      setError(mapped.message);
      setFieldErrors(mapped.fieldErrors);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {pageTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create dated tenant license windows with one overall active-user cap and optional per-role limits.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(`${TENANT_BASE_PATH}/${tenantId}`)}>
              Back to Tenant
            </Button>
            {isEdit && loadedLicense?.can_edit ? (
              <Button color="error" startIcon={<DeleteOutlineOutlinedIcon />} onClick={handleDelete} disabled={saving}>
                Delete
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Tenant Context
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {tenant?.display_name || '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tenant?.key || '-'} • {tenant?.org_code || '-'}
                </Typography>

                {currentLicense ? (
                  <Alert severity={licenseStatusColor(licenseStatus) === 'error' ? 'error' : 'info'}>
                    Current license: {formatLicenseWindow(currentLicense)} ({licenseStatusLabel(licenseStatus)})
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    No active license window is configured for this tenant.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Paper component="form" variant="outlined" sx={{ p: 2 }} onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  License Window
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Valid From"
                      type="date"
                      fullWidth
                      value={form.valid_from}
                      onChange={(event) => setForm((previous) => ({ ...previous, valid_from: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldErrors.valid_from)}
                      helperText={fieldErrors.valid_from}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Valid To"
                      type="date"
                      fullWidth
                      value={form.valid_to}
                      onChange={(event) => setForm((previous) => ({ ...previous, valid_to: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldErrors.valid_to)}
                      helperText={fieldErrors.valid_to}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Total Active Users"
                      type="number"
                      fullWidth
                      value={form.total_active_users_limit}
                      onChange={(event) => setForm((previous) => ({ ...previous, total_active_users_limit: event.target.value }))}
                      inputProps={{ min: 1 }}
                      error={Boolean(fieldErrors.total_active_users_limit)}
                      helperText={fieldErrors.total_active_users_limit || 'Overall active-user cap for the tenant.'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notes"
                      fullWidth
                      multiline
                      minRows={3}
                      value={form.notes}
                      onChange={(event) => setForm((previous) => ({ ...previous, notes: event.target.value }))}
                      error={Boolean(fieldErrors.notes)}
                      helperText={fieldErrors.notes || 'Optional internal renewal or contract notes.'}
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Per-Role Caps
                  </Typography>
                  <Button startIcon={<AddOutlinedIcon />} onClick={addRoleLimit}>
                    Add Role Limit
                  </Button>
                </Stack>

                {form.role_limits.length === 0 ? (
                  <Alert severity="info">
                    Role caps are optional. Add them only for roles such as <code>hr_admin</code> or <code>manager</code>.
                  </Alert>
                ) : null}

                <Stack spacing={1.5}>
                  {form.role_limits.map((roleLimit, index) => (
                    <Paper key={`role-limit-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Role"
                            fullWidth
                            value={roleLimit.role}
                            onChange={(event) => updateRoleLimit(index, 'role', event.target.value)}
                            placeholder="manager"
                            error={Boolean(fieldErrors[`role_limits.${index}.role`])}
                            helperText={fieldErrors[`role_limits.${index}.role`] || 'Use the same role keys stored in users.role.'}
                          />
                        </Grid>
                        <Grid item xs={10} md={5}>
                          <TextField
                            label="Active User Limit"
                            type="number"
                            fullWidth
                            value={roleLimit.active_user_limit}
                            onChange={(event) => updateRoleLimit(index, 'active_user_limit', event.target.value)}
                            inputProps={{ min: 1 }}
                            error={Boolean(fieldErrors[`role_limits.${index}.active_user_limit`])}
                            helperText={fieldErrors[`role_limits.${index}.active_user_limit`]}
                          />
                        </Grid>
                        <Grid item xs={2} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <IconButton color="error" onClick={() => removeRoleLimit(index)}>
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button color="inherit" onClick={() => navigate(`${TENANT_BASE_PATH}/${tenantId}`)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />} disabled={saving}>
                    {saving ? 'Saving...' : isEdit ? 'Update License' : 'Create License'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
